import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Col, Layout, Row } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import {
  ForResetPasswordValues,
  getCodeForResetPassword,
  saveForResetPassword,
} from 'core/application/account/forgetPassword'
import store from 'core/application/commons/localStorage'

import PasswordInput from 'common/components/dataEntry/formik/FormikPasswordField'
import Input from 'common/components/dataEntry/formik/FormikStyledInputField'
import notification from 'common/components/feedback/Notification'
import Button from 'common/components/general/Button'
import { generalPasswordRegx } from 'common/components/utils/Regx'

import useUrlQueryParam from 'common/hooks/useUrlQueryParam'

import { commandRequest } from 'common/RequestUtils'

import { LOGIN_PATH } from '../login/Login'

const formValidator = (translate: any) =>
  Yup.object({
    email: Yup.string()
      .email(translate('admin-login.errors.valid-email'))
      .required(translate('admin-login.errors.required-email')),
    newPassword: Yup.string()
      .required(translate('general.required'))
      .min(8, translate('user-header.change-password-form.errors.password-regex'))
      .matches(generalPasswordRegx, translate('user-header.change-password-form.errors.password-regex')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], translate('user-header.change-password-form.errors.confirm-password'))
      .required(translate('general.required')),
  })

interface ResetPasswordFormValues {
  email: string
  newPassword: string
  confirmPassword: string
}

const ResetPassword = () => {
  const [initialValues, setInitialValues] = useState<ResetPasswordFormValues>({
    email: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [loading, setLoading] = useState<boolean>(false)
  const { t: translate } = useTranslation()
  let history = useHistory()
  let code = useUrlQueryParam('code')

  useEffect(() => {
    if (!code) history.push(LOGIN_PATH)
  }, [code])

  useEffect(() => {
    if (code) {
      getCodeForResetPassword(code)
        .then((response) => {
          setInitialValues(response)
        })
        .catch(() => {
          history.push(LOGIN_PATH)
        })
    } else {
      history.push(LOGIN_PATH)
    }
  }, [])

  const onSubmit = async (values: ResetPasswordFormValues) => {
    setLoading(true)

    let dataForSubmit: ForResetPasswordValues = {
      code: code ? encodeURIComponent(code) : '',
      email: values.email,
      password: values.newPassword,
    }

    let result = await commandRequest(() => saveForResetPassword(dataForSubmit))
    setLoading(false)
    if (!result.errors) {
      notification.open({
        message: translate('admin-login.reset-success-msg'),
        type: 'success',
      })
      store.setToken(result.token)

      window.location.href = '/'
    } else {
      notification.open({
        message: result.errors[0],
        type: 'error',
      })
    }
  }

  return (
    <Layout>
      <Formik
        enableReinitialize={true}
        validationSchema={formValidator(translate)}
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <Row justify="center">
              <Col span={24}>
                <Row>
                  <Col
                    sm={{ span: 24, offset: 3 }}
                    xs={{ span: 24 }}
                    md={{ span: 18, offset: 5 }}
                    lg={{ span: 15, offset: 7 }}
                    xl={{ span: 10, offset: 7 }}
                  >
                    <div>
                      <h3>Set New Password</h3>
                      <p className="font-16">{translate('admin-login.forgot-password-instructions')}</p>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col
                    sm={{ span: 24, offset: 3 }}
                    xs={{ span: 24 }}
                    md={{ span: 18, offset: 5 }}
                    lg={{ span: 15, offset: 7 }}
                    xl={{ span: 10, offset: 7 }}
                  >
                    <Input disabled label={translate('admin-login.labels.email')} name="email" />
                  </Col>

                  <Col
                    sm={{ span: 24, offset: 3 }}
                    xs={{ span: 24 }}
                    md={{ span: 18, offset: 5 }}
                    lg={{ span: 15, offset: 7 }}
                    xl={{ span: 10, offset: 7 }}
                  >
                    <PasswordInput
                      label={translate('admin-login.labels.new-password')}
                      name="newPassword"
                      popover={
                        window.innerWidth > 769 ||
                        (formik.values?.newPassword !== '' &&
                          formik.values.newPassword !== undefined &&
                          !formik.values?.newPassword.match(generalPasswordRegx) &&
                          true)
                      }
                      popoverPlacement={window.innerWidth < 767 && 'topRight'}
                      className="input-with-suffix__border-botton-only"
                      setPassword={() => {}}
                    />
                  </Col>
                  <Col
                    sm={{ span: 24, offset: 3 }}
                    xs={{ span: 24 }}
                    md={{ span: 18, offset: 5 }}
                    lg={{ span: 15, offset: 7 }}
                    xl={{ span: 10, offset: 7 }}
                  >
                    <PasswordInput
                      label={translate('admin-login.labels.confirm-password')}
                      name="confirmPassword"
                      className="input-with-suffix__border-botton-only"
                      setPassword={() => {}}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col
                    sm={{ span: 24, offset: 3 }}
                    xs={{ span: 24 }}
                    md={{ span: 18, offset: 5 }}
                    lg={{ span: 15, offset: 7 }}
                    xl={{ span: 10, offset: 7 }}
                  >
                    <Button htmlType="submit" type="primary" className="button__large" loading={loading}>
                      {translate('admin-login.reset-password-button')}
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </form>
        )}
      </Formik>
    </Layout>
  )
}

export default ResetPassword
